import NexusStructuredReply from "../ai/components/NexusStructuredReply";

export function IconSparkles({ className = "w-5 h-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
      />
    </svg>
  );
}

export function IconSearch({ className = "w-5 h-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path strokeLinecap="round" d="M20 20l-3-3" />
    </svg>
  );
}

export function IconBolt({ className = "w-5 h-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}

export function IconChart({ className = "w-5 h-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 20h18M7 16v-4M12 16V8M17 16V4" />
    </svg>
  );
}

export function IconUsers({ className = "w-5 h-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"
      />
    </svg>
  );
}

export function IconDocument({ className = "w-5 h-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zM14 2v6h6M16 13H8M16 17H8M10 9H8"
      />
    </svg>
  );
}

export function IconBuilding({ className = "w-5 h-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-4h6v4"
      />
    </svg>
  );
}

export function formatParagraphs(text) {
  return String(text || "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
}

/** Split a line like "* A * B * C" into bullet items (not markdown **bold**). */
function splitInlineAsteriskBullets(line) {
  const trimmed = line.trim();
  if (!trimmed.includes("*") || trimmed.includes("**")) return null;
  const parts = trimmed
    .split(/\s+\*\s+/)
    .map((p) => p.replace(/^\*\s*/, "").trim())
    .filter(Boolean);
  return parts.length >= 2 ? parts : null;
}

function safeHref(url) {
  const value = String(url || "").trim();
  if (value.startsWith("/") && !value.startsWith("//")) return value;
  if (/^https?:\/\//i.test(value)) return value;
  return "";
}

/** Render **bold** segments and markdown links in plain text. */
function renderInlineFormatting(text) {
  const raw = String(text || "");
  if (!raw.includes("**") && !raw.includes("](")) return raw;
  const pattern = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
  const chunks = [];
  let last = 0;
  let match = pattern.exec(raw);
  let index = 0;
  while (match) {
    if (match.index > last) chunks.push(raw.slice(last, match.index));
    const token = match[0];
    if (token.startsWith("**")) {
      chunks.push(
        <strong key={`b-${index}`} className="font-semibold text-slate-900">
          {token.slice(2, -2)}
        </strong>
      );
    } else {
      const link = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      const href = safeHref(link?.[2]);
      if (href) {
        chunks.push(
          <a
            key={`a-${index}`}
            href={href}
            className="font-medium text-[#004f73] underline underline-offset-2"
          >
            {link[1]}
          </a>
        );
      } else {
        chunks.push(link?.[1] || token);
      }
    }
    last = match.index + token.length;
    index += 1;
    match = pattern.exec(raw);
  }
  if (last < raw.length) chunks.push(raw.slice(last));
  return chunks.length ? chunks : raw;
}

function parseTableCells(line) {
  const trimmed = String(line || "").trim();
  if (!trimmed.startsWith("|")) return null;
  const parts = trimmed.split("|");
  const end = trimmed.endsWith("|") ? -1 : undefined;
  const cells = parts.slice(1, end).map((cell) => cell.trim());
  if (cells.length < 2) return null;
  return cells;
}

function isTableSeparator(cells) {
  return cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

/**
 * Parse insight answer text into paragraphs and bullet lists for display.
 */
export function parseInsightAnswerBlocks(text) {
  const blocks = [];
  const lines = String(text || "").split("\n");
  let paragraphLines = [];
  let listItems = [];
  let tableRows = [];

  const flushTable = () => {
    if (!tableRows.length) return;
    const rows = tableRows.filter((cells) => !isTableSeparator(cells));
    tableRows = [];
    if (rows.length < 2) {
      if (rows.length === 1) {
        blocks.push({ type: "p", text: rows[0].join(" | ") });
      }
      return;
    }
    blocks.push({ type: "table", columns: rows[0], rows: rows.slice(1) });
  };

  const flushParagraph = () => {
    if (!paragraphLines.length) return;
    const joined = paragraphLines.join("\n").trim();
    paragraphLines = [];
    if (!joined) return;
    const inline = splitInlineAsteriskBullets(joined);
    if (inline) {
      if (listItems.length) {
        blocks.push({ type: "ul", items: [...listItems] });
        listItems = [];
      }
      blocks.push({ type: "ul", items: inline });
      return;
    }
    blocks.push({ type: "p", text: joined });
  };

  const flushList = () => {
    if (!listItems.length) return;
    blocks.push({ type: "ul", items: [...listItems] });
    listItems = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();
    const tableCells = parseTableCells(trimmed);
    if (tableCells) {
      flushParagraph();
      flushList();
      tableRows.push(tableCells);
      continue;
    }
    flushTable();
    const bullet = trimmed.match(/^[-•]\s+(.+)$/) || trimmed.match(/^\d+\.\s+(.+)$/);
    const starBullet =
      !trimmed.includes("**") && /^\*\s+/.test(trimmed) ? trimmed.match(/^\*\s+(.+)$/) : null;
    if (bullet || starBullet) {
      flushParagraph();
      listItems.push((bullet || starBullet)[1].trim());
      continue;
    }
    if (!trimmed) {
      flushList();
      flushParagraph();
      continue;
    }
    const inline = splitInlineAsteriskBullets(trimmed);
    if (inline) {
      flushParagraph();
      flushList();
      blocks.push({ type: "ul", items: inline });
      continue;
    }
    flushList();
    paragraphLines.push(trimmed);
  }
  flushTable();
  flushList();
  flushParagraph();
  return blocks;
}

export function InsightAnswerBody({ text }) {
  const blocks = parseInsightAnswerBlocks(text);
  if (!blocks.length) return null;
  return (
    <>
      {blocks.map((block, i) => {
        if (block.type === "ul") {
          return (
            <ul key={i} className={`list-disc pl-5 space-y-1.5 ${i > 0 ? "mt-3" : ""}`}>
              {block.items.map((item, j) => (
                <li key={`${i}-${j}`} className="leading-relaxed">
                  {renderInlineFormatting(item)}
                </li>
              ))}
            </ul>
          );
        }
        if (block.type === "table") {
          return (
            <div key={i} className={`overflow-x-auto rounded-xl border border-slate-200 ${i > 0 ? "mt-3" : ""}`}>
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50/80">
                    {block.columns.map((column) => (
                      <th key={column} className="text-left px-3 py-2 font-semibold text-slate-600">
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, rowIndex) => (
                    <tr key={`${i}-${rowIndex}`} className="border-t border-slate-100">
                      {block.columns.map((column, colIndex) => (
                        <td key={`${column}-${colIndex}`} className="px-3 py-2 text-slate-800">
                          {renderInlineFormatting(row[colIndex] || "—")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return (
          <p key={i} className={`leading-relaxed ${i > 0 ? "mt-3" : ""}`}>
            {renderInlineFormatting(block.text)}
          </p>
        );
      })}
    </>
  );
}

export function AssistantTabs({ tabs, active, onChange }) {
  return (
    <div className="flex p-1 rounded-xl bg-slate-900/5 gap-1" role="tablist">
      {tabs.map((tab) => {
        const isActive = active === tab.id;
        const Icon = tab.icon;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
              isActive
                ? "bg-white text-[#004f73] shadow-sm ring-1 ring-slate-200/80"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            {Icon ? <Icon className="w-4 h-4 shrink-0" /> : null}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export function WorkflowStepper({ step, total = 3, label }) {
  if (!step) return null;
  const pct = Math.min(100, Math.round((step / total) * 100));
  return (
    <div className="rounded-xl bg-white border border-slate-200/80 px-4 py-3 shadow-sm">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Workflow</span>
        <span className="text-xs font-medium text-[#004f73]">
          Step {step} of {total}
          {label ? ` · ${label}` : ""}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[#004f73] to-[#0a7ea4] transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function AlertStrip({ children, variant = "info" }) {
  const styles =
    variant === "warning"
      ? "bg-amber-50 border-amber-200/80 text-amber-950"
      : "bg-sky-50 border-sky-200/80 text-sky-950";
  return (
    <div className={`rounded-xl border px-3.5 py-2.5 text-sm leading-relaxed ${styles}`}>{children}</div>
  );
}

export function QuickActionTile({ icon: Icon, label, description, onClick, disabled, active = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`group flex-1 min-w-[9.5rem] text-left rounded-xl border px-3 py-2.5 shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004f73]/40 ${
        active
          ? "border-[#004f73]/45 bg-[#004f73]/[0.08] ring-2 ring-[#004f73]/15"
          : "border-slate-200/90 bg-white hover:border-[#004f73]/30"
      }`}
    >
      <div className="flex items-start gap-2.5">
        <div className="w-8 h-8 shrink-0 rounded-lg bg-gradient-to-br from-[#004f73]/12 to-[#0a7ea4]/8 text-[#004f73] flex items-center justify-center group-hover:scale-[1.02] transition-transform">
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900 leading-tight">{label}</p>
          <p className="text-[11px] text-slate-500 leading-snug mt-0.5 line-clamp-2">{description}</p>
        </div>
      </div>
    </button>
  );
}

export function InsightResultCard({
  query,
  answer,
  loading,
  label = "Insight",
  relatedPages = [],
  dataSources = [],
  structured = null,
}) {
  if (!query && !answer && !loading) return null;
  const Icon = label === "NEXUS" ? IconSparkles : IconChart;
  const sources = Array.isArray(dataSources) ? dataSources.filter(Boolean) : [];
  return (
    <article className="rounded-2xl border border-slate-200/90 bg-white overflow-hidden shadow-sm">
      <div className="px-4 py-2.5 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#004f73]">
          <Icon className="w-3.5 h-3.5" />
          {label}
        </span>
        {query ? (
          <span className="text-xs text-slate-500 truncate flex-1 min-w-0" title={query}>
            {query}
          </span>
        ) : null}
      </div>
      <div className="px-4 py-4 text-sm text-slate-800 leading-relaxed min-h-[4rem]">
        {loading ? (
          <div className="space-y-2.5 animate-pulse" aria-busy="true">
            <div className="h-3 bg-slate-100 rounded w-full" />
            <div className="h-3 bg-slate-100 rounded w-11/12" />
            <div className="h-3 bg-slate-100 rounded w-4/5" />
          </div>
        ) : structured ? (
          <NexusStructuredReply
            structured={structured}
            fallbackText={answer}
            relatedPages={relatedPages}
            dataSources={sources}
          />
        ) : (
          <>
            <InsightAnswerBody text={answer} />
            {!loading && sources.length ? (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {sources.map((src) => (
                  <span
                    key={src}
                    className="text-[10px] font-medium uppercase tracking-wide text-slate-500 bg-slate-100 rounded px-2 py-0.5"
                  >
                    {src}
                  </span>
                ))}
              </div>
            ) : null}
            {!loading && relatedPages?.length ? (
              <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                {relatedPages.map((page) => (
                  <a
                    key={page.path}
                    href={page.path}
                    className="text-xs font-medium text-[#004f73] bg-[#004f73]/[0.06] border border-[#004f73]/20 rounded-full px-3 py-1 hover:bg-[#004f73]/10 transition-colors"
                    title={page.reason || page.label}
                  >
                    {page.label} →
                  </a>
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>
    </article>
  );
}
